import {
	Disclosure,
	DisclosureButton,
	DisclosurePanel,
} from '@headlessui/react'
import {
	data,
	Link,
	Links,
	Meta,
	NavLink,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import sonnerStyleSheetUrl from 'sonner/dist/styles.css?url'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import { UserGuidesLink } from './components/user-guides-link.tsx'
import { UserNotifications } from './components/user-notifications.tsx'
import { DEFAULT_ORGANIZATION_SETTINGS } from './routes/organizations+/organization-settings.ts'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import fontStyleSheetUrl from './styles/stylesheet.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import {
	getCurrentOrganizationId,
	getUserId,
	logout,
} from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { cn, combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getOrganizationSettings } from './utils/organization.server.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import {
	type PermissionString,
	useOptionalUser,
	userHasAnyPermission,
	userHasPermission,
} from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
		{ rel: 'stylesheet', href: fontStyleSheetUrl },
		{ rel: 'stylesheet', href: sonnerStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'MediSis' : 'Error | MediSis' },
		{ name: 'description', content: `Your own medic system` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})
	const organizationId = await time(() => getCurrentOrganizationId(request), {
		timings,
		type: 'getCurrentOrganizationId',
		desc: 'getCurrentOrganizationId in root',
	})

	const organizationSettings = organizationId
		? await getOrganizationSettings(organizationId)
		: DEFAULT_ORGANIZATION_SETTINGS

	const user = userId
		? await time(
				async () => {
					const userData = await prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							organizationUsers: {
								select: {
									organization: { select: { id: true, name: true } },
									roles: {
										select: {
											name: true,
											description: true,
											permissions: {
												select: { entity: true, action: true, access: true },
											},
										},
									},
								},
							},
						},
						where: { id: userId },
					})

					// Collect all roles from all organizations the user belongs to
					// Merge permissions from roles with the same name across organizations
					const allRolesMap = new Map<
						string,
						{
							name: string
							description: string
							permissions: Array<{
								entity: string
								action: string
								access: string
							}>
						}
					>()
					userData.organizationUsers.forEach((orgUser) => {
						orgUser.roles.forEach((role) => {
							if (!allRolesMap.has(role.name)) {
								// First occurrence of this role name - initialize with its permissions
								allRolesMap.set(role.name, {
									name: role.name,
									description: role.description,
									permissions: [...role.permissions],
								})
							} else {
								// Merge permissions from this role instance
								const existingRole = allRolesMap.get(role.name)!
								const permissionSet = new Set(
									existingRole.permissions.map(
										(p) => `${p.entity}:${p.action}:${p.access}`,
									),
								)
								role.permissions.forEach((permission) => {
									const key = `${permission.entity}:${permission.action}:${permission.access}`
									if (!permissionSet.has(key)) {
										existingRole.permissions.push(permission)
										permissionSet.add(key)
									}
								})
							}
						})
					})
					const roles = Array.from(allRolesMap.values())

					let organizations = userData.organizationUsers.map(
						(orgUser) => orgUser.organization,
					)

					return {
						id: userData.id,
						name: userData.name,
						username: userData.username,
						image: userData.image,
						organizations,
						roles,
					}
				},
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			organizationSettings,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden antialiased`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	useToast(data.toast)

	const { enableDigitalAgenda, enableBilling } = data.organizationSettings

	const canEditReceivables = userHasPermission(
		user,
		'update:healthNetworkReceivable:any',
	)

	const canReadAppointments = userHasPermission(user, 'read:appointment')
	const canReadPatients = userHasPermission(user, 'read:core->patients')

	const canReadInvoices = userHasPermission(user, 'read:billing->invoices')

	const canReadReports = userHasAnyPermission(user, [
		'read:serviceRecord:any',
		'read:healthNetworkReceivable:any',
		'read:core->reports->pricing',
		...(enableDigitalAgenda
			? (['read:appointment:any'] as PermissionString[])
			: []),
		...(enableBilling
			? ([
					'read:billing->reports->cashClosing:any',
					'read:billing->reports->sales:any',
					'read:billing->reports->invoices-by-specialty:any',
				] as PermissionString[])
			: []),
	])

	const canReadSettings = userHasAnyPermission(user, [
		'read:specialty:any',
		'read:doctor:any',
		'read:paymentMethod:any',
		'read:healthNetwork:any',
		'read:room:any',
		'read:branch:any',
		'read:paymentTerminal:any',
		'read:organization:any',
		'read:user:any',
	])

	const isOnMarketingOrAuthPage =
		matches.find((m) => m.id.includes('marketing')) ||
		matches.find((m) => m.id.includes('auth'))

	const navigation = [
		...(enableDigitalAgenda && canReadAppointments
			? [
					{
						name: 'Agenda',
						href: '/agenda/daily',
						current: false,
					},
				]
			: []),
		...(canReadPatients
			? [
					{
						name: 'Pacientes',
						href: '/patients',
						current: false,
					},
				]
			: []),
		...(enableBilling && canReadInvoices
			? [
					{
						name: 'Facturación',
						href: '/billing/dashboard',
						current: false,
					},
				]
			: []),
		...(canEditReceivables
			? [
					{
						name: 'Cuentas por cobrar',
						href: '/receivables',
						current: false,
					},
				]
			: []),
		...(canReadReports
			? [{ name: 'Reportes', href: '/reports', current: false }]
			: []),
		...(canReadSettings
			? [{ name: 'Configuración', href: '/settings', current: false }]
			: []),
	]

	return (
		<>
			<div className="flex min-h-screen flex-col justify-between">
				{!isOnMarketingOrAuthPage ? (
					<header className="pb-6 print:hidden">
						<Disclosure as="nav" className="bg-navbar text-navbar-foreground">
							{({ open }) => (
								<>
									<div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
										<div className="relative flex h-16 items-center justify-between md:items-stretch">
											{/* Mobile & Tablet menu button (show on <lg) */}
											<div className="absolute inset-y-0 left-0 flex items-center lg:hidden">
												{/* Menu button */}
												<DisclosureButton className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
													<span className="absolute -inset-0.5" />
													<span className="sr-only">Open main menu</span>
													{open ? (
														<Icon
															name="cross-1"
															className="block h-6 w-6"
															aria-hidden="true"
														/>
													) : (
														<Icon
															name="hamburger-menu"
															className="block h-6 w-6"
															aria-hidden="true"
														/>
													)}
												</DisclosureButton>
											</div>
											<div className="flex flex-1 items-center justify-center md:h-full md:items-stretch md:justify-start">
												<div className="flex flex-shrink-0 items-center md:ml-16 md:items-center">
													<Link to="/dashboard">
														<div className="text-2xl font-bold">
															Medi
															<span className="text-primary">Sis</span>
														</div>
													</Link>
												</div>
												{/* Main nav for laptops+ (lg and above) */}
												<div className="hidden lg:ml-6 lg:flex lg:h-full">
													<div className="flex h-full items-stretch space-x-4">
														{navigation.map((item) => (
															<NavLink
																key={item.name}
																className={({ isActive }) =>
																	cn(
																		isActive
																			? 'border-b-3 border-primary font-bold'
																			: 'text-nav-foreground/60 hover:text-primary',
																		'flex h-full select-none items-center px-3 text-sm',
																	)
																}
																to={item.href}
															>
																{item.name}
															</NavLink>
														))}
													</div>
												</div>
											</div>
											<div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2 lg:static lg:inset-auto lg:ml-6 lg:gap-2 lg:pr-0">
												{user ? (
													<>
														<UserNotifications />
														<UserGuidesLink />
														<UserDropdown />
													</>
												) : (
													<Button asChild size="sm">
														<Link to="/login">Ingresar</Link>
													</Button>
												)}
												{/* <ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} /> */}
											</div>
										</div>
									</div>
									{/* For mobile and tablet: show the panel for <lg (up through md) */}
									<DisclosurePanel className="lg:hidden">
										<div className="space-y-1 px-2 pb-3 pt-2">
											{navigation.map((item) => (
												<DisclosureButton
													key={item.name}
													as="a"
													href={item.href}
													className={cn(
														item.current
															? 'bg-gray-900 text-white'
															: 'text-gray-300 hover:bg-gray-700 hover:text-white',
														'block select-none rounded-md px-3 py-2 text-base font-medium',
													)}
													aria-current={item.current ? 'page' : undefined}
												>
													{item.name}
												</DisclosureButton>
											))}
										</div>
									</DisclosurePanel>
								</>
							)}
						</Disclosure>
					</header>
				) : null}

				<div className="flex-1">
					<Outlet />
				</div>
			</div>
			<EpicToaster
				key="global-toaster"
				closeButton
				position="top-right"
				richColors
				theme={theme}
			/>
			<EpicProgress />
		</>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
